import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MeasurementCorrectionPanel.css';

const ZONES = ['chest', 'waist', 'hips'];
const MIN_CM = 40;
const MAX_CM = 200;

const getCenter = (measurements, key) => {
  const min = measurements?.[`${key}_min`];
  const max = measurements?.[`${key}_max`];
  if (min == null || max == null) return '';
  return Math.round((min + max) / 2);
};

function MeasurementCorrectionPanel({ gender, computedMeasurements, onCancel, onSubmit, isSubmitting, error }) {
  const { t } = useTranslation('results');
  const { t: tc } = useTranslation('common');

  const genderCode = gender === 'homme' ? 'H' : 'F';

  // Valeurs de départ figées à l'ouverture — sert de référence pour ne remonter
  // que les zones réellement modifiées par l'utilisateur.
  const [initialValues] = useState(() =>
    ZONES.reduce((acc, key) => ({ ...acc, [key]: getCenter(computedMeasurements, key) }), {})
  );
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const handleChange = (key, raw) => {
    setValues((prev) => ({ ...prev, [key]: raw }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const nextErrors = {};
    ZONES.forEach((key) => {
      if (values[key] === '' || values[key] == null) return;
      const num = Number(values[key]);
      if (Number.isNaN(num) || num < MIN_CM || num > MAX_CM) {
        nextErrors[key] = t('correction.validationError', { min: MIN_CM, max: MAX_CM });
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const manualValues = {};
    ZONES.forEach((key) => {
      const num = Number(values[key]);
      if (!Number.isNaN(num) && num !== Number(initialValues[key])) {
        manualValues[key] = num;
      }
    });

    if (Object.keys(manualValues).length === 0) {
      onCancel();
      return;
    }

    onSubmit(manualValues);
  };

  return (
    <div className="correction-panel">
      <h3 className="correction-panel__title">{t('correction.title')}</h3>
      <p className="correction-panel__description">{t('correction.description')}</p>

      {ZONES.map((key) => (
        <div key={key} className="correction-zone">
          <img
            src={`/body_part/${key}_${genderCode}_slim.jpg`}
            alt=""
            className="correction-zone__img"
          />
          <div className="correction-zone__field">
            <label className="form-label">{tc(`mannequin.${key}`)}</label>
            <div className="input-with-unit">
              <input
                type="number"
                value={values[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                min={MIN_CM}
                max={MAX_CM}
                className={`form-input ${errors[key] ? 'error' : ''}`}
              />
              <span className="unit">{tc('units.cm')}</span>
            </div>
            {errors[key] && <span className="error-message">{errors[key]}</span>}
          </div>
        </div>
      ))}

      {error && <p className="error-message">{error}</p>}

      <div className="correction-panel__actions">
        <button className="btn btn-secondary" onClick={onCancel} disabled={isSubmitting}>
          {tc('buttons.back')}
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? tc('loading') : t('correction.submitButton')}
        </button>
      </div>
    </div>
  );
}

export default MeasurementCorrectionPanel;
